import {isIdNum} from '@/util/regex';

export const YEAR_FORMAT = 'yyyy';
export const MONTH_FORMAT = 'yyyy-MM';
export const DATE_FORMAT = 'yyyy-MM-dd';
export const DATE_TIME_SHORT_FORMAT = 'yyyy-MM-dd HH:mm';
export const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm:ss';
export const DATE_TIME_FULL_FORMAT = 'yyyy-MM-dd HH:mm:ss.S';

/**
 * 文件体积单位
 * @type {{label: string, value: string}[]}
 */
export const FILE_SIZE_UNITS = [
  {label: 'KB', value: 'KB'},
  {label: 'MB', value: 'MB'},
  {label: 'GB', value: 'GB'}
];

/**
 * 大写字母
 * @type {string[]}
 */
export const UPPER_CASE_LETTER = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
/**
 * 小写字母
 * @type {string[]}
 */
export const LOWER_CASE_LETTER = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'];
/**
 * 数字
 * @type {string[]}
 */
export const DIGITAL_LETTER = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
/**
 * 特殊字符
 * @type {string[]}
 */
export const SPECIAL_LETTER = ['!', '@', '#', '$', '%', '^', '&', '*'];
/**
 * 六位数密码
 * @type {RegExp}
 */
export const simplePasswordReg = /^(!|@|#|\$|%|\\|\^|&|\*|\+|\?|\{|}|\||\w){6,}$/;

/**
 * @desc 判断value是不是一个字符串
 * @param value
 * @return {boolean}
 */
export function isString (value) {
  return (typeof value).toLowerCase() === 'string' || (value instanceof String);
}
/**
 * @desc 判断value是不是一个布尔值
 * @param value
 * @return {boolean}
 */
export function isBoolean (value) {
  return (typeof value).toLowerCase() === 'boolean' || (value instanceof Boolean);
}
/**
 * @desc 判断value是不是一个数值
 * @param value
 * @return {boolean}
 */
export function isNumber(value) {
  return (typeof value).toLowerCase() === 'number' || (value instanceof Number);
}
/**
 * @desc 判断value是不是一个函数
 * @param value
 * @return {boolean}
 */
export function isFunction (value) {
  return (typeof value).toLowerCase() === 'function' || (value instanceof Function);
}
/**
 * @desc 判断value是不是一个对象
 * @param value
 * @return {boolean}
 */
export function isObject (value) {
  // (typeof value).toLowerCase() === 'object' 当value是null,也会返回true
  // instanceof的方式会排除null的情况
  return (value instanceof Object);
}
/**
 * 是不是一个普通的对象
 * @param value
 * @return {boolean}
 */
export function isPlainObject(value) {
  if (!isObject(value)) {
    return false;
  }
  let proto = value;
  // 一直往上找原型，直到未null, 此时proto是Object
  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }
  return Object.getPrototypeOf(value) === proto;
}

/**
 * 判断参数是不是一个空对象
 * @param value
 * @return {boolean}
 */
export function isEmptyObject(value) {
  return isEmpty(value) || !isObject(value) || Object.keys(value).length < 1;
}

/**
 * 判断传入的参数是否为null，undefined或者空字符串
 * @param arg
 * @return {boolean}
 */
export function isEmpty (arg) {
  return arg === null || arg === undefined || arg === '';
}
export function isTrimEmpty (str) {
  if (isEmpty(str)) {
    return true;
  }
  return str.trim() === '';
}
/**
 * 判断传入的参数不为为null，undefined或者空字符串
 * @param arg
 * @return {boolean}
 */
export function isNotEmpty (arg) {
  return !isEmpty(arg);
}
/**
 * 生成随机uuid (不能保证全局唯一，需优化)
 * by TanJY
 */
export function uuid ({simple = false} = {}) {
  let d = new Date().getTime();
  if (window.performance && typeof window.performance.now === 'function') {
    d += performance.now(); // use high-precision timer if available
  }
  let uuidStr = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
  if (simple) {
    uuidStr = uuidStr.replace(/-/g, '');
  }
  return uuidStr;
}
/**
 * @desc yyyy-MM-dd, yyyy-MM-dd hh:mm:ss, yyyy/MM/dd, yyyy/MM/dd hh:mm:ss这样个是的字符串转换成date类型
 * @author LiangYJ
 * @param str
 */
export function stringToDate (str) {
  if (!str) {
    return null;
  }
  return new Date(str.replace(/-/ig, '/'));
}
/**
 * 根据格式，再次格式化日期时间字符串
 * @param time
 * @param cFormat
 * @return {string|null}
 */
export function parseTime(time, cFormat) {
  if (arguments.length === 0 || !time) {
    return null;
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}';
  let date;
  if (typeof time === 'object') {
    date = time;
  } else {
    if (('' + time).length === 10) {
      time = parseInt(time) * 1000;
    }
    date = new Date(time);
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  };
  return format.replace(/{(y|m|d|h|i|s|a)+}/g, (result, key) => {
    let value = formatObj[key];
    if (key === 'a') {
      return ['一', '二', '三', '四', '五', '六', '日'][value - 1];
    }
    if (result.length > 0 && value < 10) {
      value = '0' + value;
    }
    return value || 0;
  });
}
/**
 * 将数字或字符串转成日期对象
 * @param obj
 * @return {null|Date}
 */
export function toDate (obj) {
  if (obj === undefined || obj === null) {
    return null;
  }
  if (obj instanceof Date) {
    return obj;
  }
  if ((typeof obj).toLowerCase() === 'number' || obj instanceof Number) {
    return new Date(obj);
  }
  if ((typeof obj).toLowerCase() === 'string' || obj instanceof String) {
    // 对应毫秒数字符串也要进行判断
    if (Number.isNaN(Number(obj))) {
      return stringToDate(obj);
    } else {
      return new Date(parseInt(obj));
    }
  }
  return null;
}
/**
 * 日期格式化
 */
export function dateFormat (fmt, date) {
  const o = {
    'M+': date.getMonth() + 1,
    'd+': date.getDate(),
    'H+': date.getHours(),
    'm+': date.getMinutes(),
    's+': date.getSeconds(),
    'q+': Math.floor((date.getMonth() + 3) / 3),
    S: date.getMilliseconds()
  };
  if (/(y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
  }
  for (const k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (RegExp.$1.length === 1) ? (o[k]) : (('00' + o[k]).substr(('' + o[k]).length)));
    }
  }
  return fmt;
}

/**
 * 格式化成 yyyy-MM-dd HH:mm:ss
 * @param value
 * @return {*}
 */
export function formatDateTime (value) {
  if (value === undefined || value === null || value === '') {
    return value;
  }
  let date;
  if (!(value instanceof Date)) {
    if (isNaN(value)) {
      return value;
    }
    // 毫秒要转成date
    date = new Date(Number(value));
  } else {
    date = value;
  }
  return dateFormat(DATE_TIME_FORMAT, date);
}

/**
 * @desc 判断运行环境的系统类型
 * @author LiangYJ
 * @returns {string}
 * 2020-03-25
 */
export function detectOS () {
  const sUserAgent = navigator.userAgent;
  const isWin = (navigator.platform === 'Win32') || (navigator.platform === 'Windows');
  const isMac = (navigator.platform === 'Mac68K') || (navigator.platform === 'MacPPC') || (navigator.platform === 'Macintosh') || (navigator.platform === 'MacIntel');
  if (isMac) { return 'Mac'; }
  const isUnix = (navigator.platform === 'X11') && !isWin && !isMac;
  if (isUnix) { return 'Unix'; }
  const isLinux = (String(navigator.platform).indexOf('Linux') > -1);
  const matchArray = sUserAgent.toLowerCase().match(/android/);
  let bIsAndroid = false;
  if (Array.isArray(matchArray) && matchArray.length > 0) {
    // 使用for...of导致webpack报错
    for (let i = 0; i < matchArray.length; i++) {
      if (matchArray[i] === 'android') {
        bIsAndroid = true;
        break;
      }
    }
  }
  if (isLinux) {
    if (bIsAndroid) { return 'Android'; } else { return 'Linux'; }
  }
  if (isWin) {
    const isWin2K = sUserAgent.indexOf('Windows NT 5.0') > -1 || sUserAgent.indexOf('Windows 2000') > -1;
    if (isWin2K) { return 'Win2000'; }
    const isWinXP = sUserAgent.indexOf('Windows NT 5.1') > -1 ||
      sUserAgent.indexOf('Windows XP') > -1;
    if (isWinXP) { return 'WinXP'; }
    const isWin2003 = sUserAgent.indexOf('Windows NT 5.2') > -1 || sUserAgent.indexOf('Windows 2003') > -1;
    if (isWin2003) { return 'Win2003'; }
    const isWinVista = sUserAgent.indexOf('Windows NT 6.0') > -1 || sUserAgent.indexOf('Windows Vista') > -1;
    if (isWinVista) { return 'WinVista'; }
    const isWin7 = sUserAgent.indexOf('Windows NT 6.1') > -1 || sUserAgent.indexOf('Windows 7') > -1;
    if (isWin7) { return 'Win7'; }
    if (sUserAgent.indexOf('Windows NT 10.0') > -1) {
      return 'Win10';
    }
  }
  return 'other';
}
/**
 * @desc 判断运行环境的系统类型
 * @author LiangYJ
 * @returns {string}
 * 2020-03-25
 */
export function getBrowserInfo () {
  const userAgent = navigator.userAgent.toLowerCase();
  const browserInfo = {};
  // 判断是pc还是mobile
  if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent)) {
    // 微信浏览器/qq浏览器/小米
    // 荣耀al00a机型，userAgent只显示safari
    const browserArray = ['micromessenger', 'mqqbrowser', 'ucbrowser', 'miuibrowser', 'chrome', 'safari'];
    let matchArray = null;
    for (let i = 0; i < browserArray.length; i++) {
      matchArray = userAgent.match('(' + browserArray[i] + ').*?([\\d.]+)');
      if (matchArray) {
        browserInfo.browser = matchArray[1];
        browserInfo.version = matchArray[2];
        break;
      }
    }
    if (/android/.test(userAgent)) {
      browserInfo.system = 'android';
      const modelMatchArray = userAgent.match(/([a-z0-9-]+\s*)*build/);
      if (modelMatchArray) {
        browserInfo.model = modelMatchArray[0].replace(/(\s*build)/g, '');
      }
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      browserInfo.system = 'ios';
      browserInfo.model = '未知';
    } else {
      browserInfo.system = '其它';
      browserInfo.model = '未知';
    }
  } else {
    const matchArray = userAgent.match(/(msie|firefox|chrome|opera|opr|version).*?([\d.]+)/);
    browserInfo.browser = matchArray[1].replace(/version/, 'safari');
    browserInfo.version = matchArray[2];
    browserInfo.system = detectOS();
  }
  return browserInfo;
}

/**
 * 判断是不是移动端
 * @return {boolean}
 */
export function isMobile () {
  const ua = navigator.userAgent.toLowerCase();
  const items = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPad', 'iPod'];
  return items.some((n) => ua.indexOf(n.toLowerCase()) >= 0);
}

/**
 * 获取当前月份的最大值
 * @param value
 * @return {number}
 */
export function getMaxDate (value) {
  let date;
  if (value === undefined || value === null) {
    date = new Date();
  } else {
    date = new Date(value.getTime());
  }
  date.setDate(1);
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return date.getDate();
}
/**
 * @desc 首字母大写
 * @author LiangYongJia
 * @param str
 * @returns {string|*}
 */
export function capitalizeFirstLetter (str) {
  // if (!str || ((typeof str).toLowerCase() !== 'string' && !(str instanceof String))) {
  //   return str;
  // }
  if (!str || !(typeof str === 'string') || str instanceof String) {
    return str;
  }
  if (str.length === 1) {
    return str.toUpperCase();
  }
  return str.charAt(0).toUpperCase() + str.substring(1);
}
/**
 * @desc 解析日期公式 日期 y 代表年 M 代表月 d 代表日 H 代表24进制的小时 h 代表12进制的小时 m 代表分钟 s 代表秒 S 代表毫秒
 * @param str 如 yyyy-(MM-1)-dd(01) 中间不能有空格，表示上个月第一天，(MM-1)表示当前月份减去1，dd(01)表示月固定的某一天,
 * first可以用于表示每年第一个月份，每月第一天
 * last可以用于表示每年最后一个月份，每月最后一天
 * 注意：first和last只能用于xx(first)，xx(last)这种情况
 * @return {Date}
 */
export function resolveDateTimeFormula (str) {
  // obj用于存放解析后的结果
  const obj = {
    /*
    * 每个对象包含以下元素
    * contain: str中是否包含该部分
    * operator: 操作符，如+-
    * value: 括号里的值
    * */
    y: {},
    M: {},
    d: {},
    H: {},
    m: {},
    s: {}
  };
  // 先匹配（XX-1）这种情况
  let reg = /\(([a-zA-Z]+)(\+?-?)(\d+)\)/g;
  let arr = Array.from(str.matchAll(reg));
  for (const matchItem of arr) {
    let item;
    for (const key in obj) {
      if (new RegExp(key + '+').test(matchItem[1])) {
        item = obj[key];
        break;
      }
    }
    item.contain = true;
    item.operator = matchItem[2];
    item.value = parseInt(matchItem[3]);
  }
  // 匹配xx(01)这种情况
  reg = /([a-zA-Z]+)\((\d+)\)/g;
  arr = Array.from(str.matchAll(reg));
  for (const matchItem of arr) {
    let item;
    for (const key in obj) {
      if (new RegExp(key + '+').test(matchItem[1])) {
        item = obj[key];
        break;
      }
    }
    item.contain = true;
    item.value = parseInt(matchItem[2]);
  }
  // 匹配xx(first)和xx(last)这种情况
  reg = /([a-zA-Z]+)\(([a-z]+)\)/g;
  arr = Array.from(str.matchAll(reg));
  for (const matchItem of arr) {
    let item;
    for (const key in obj) {
      if (new RegExp(key + '+').test(matchItem[1])) {
        item = obj[key];
        break;
      }
    }
    item.contain = true;
    item.specialValue = matchItem[2];
  }
  reg = /[a-zA-Z]+/g;
  arr = Array.from(str.matchAll(reg));
  for (const matchItem of arr) {
    let item;
    for (const key in obj) {
      if (new RegExp(key + '+').test(matchItem[0])) {
        item = obj[key];
        // 前面匹配过的，就不需要处理
        if (item.contain) {
          continue;
        }
        break;
      }
    }
    item.contain = true;
  }
  const date = new Date();
  // 如果当前日期是平年3.30，如果月份-1,就会变成3.2，因此增加一份日期拷贝来处理这个问题
  const dateBackup = new Date(date.getTime());
  // 重置所有字段，以免出现上述情况
  date.setFullYear(0);
  date.setMonth(0);
  date.setDate(1);
  date.setMilliseconds(0);
  const year = obj.y;
  if (year.operator && year.value) {
    // eslint-disable-next-line no-new-func
    date.setFullYear(Function(`"ue strict";return ${dateBackup.getFullYear()} ${year.operator} ${year.value}`)());
  } else if (year.value) {
    date.setFullYear(year.value);
  } else {
    date.setFullYear(dateBackup.getFullYear());
  }
  const month = obj.M;
  if (month.operator && month.value) {
    // date.setMonth(eval('date.getMonth()' + month.operator + month.value));
    // eslint-disable-next-line no-new-func
    date.setMonth(Function(`"ue strict";return ${dateBackup.getMonth()} ${month.operator} ${month.value}`)());
  } else if (month.value) {
    date.setMonth(month.value - 1);
  } else if (month.specialValue === 'first') {
    date.setMonth(0);
  } else if (month.specialValue === 'last') {
    date.setMonth(11);
  } else {
    date.setMonth(dateBackup.getMonth());
  }
  const _date = obj.d;
  if (_date.operator && _date.value) {
    // eslint-disable-next-line no-new-func
    date.setDate(Function(`"ue strict";return ${dateBackup.getDate()} ${_date.operator} ${_date.value}`)());
  } else if (_date.value) {
    // 需要判断_date.value是否会超过设置月份的最大天数
    date.setDate(Math.min(getMaxDate(date), _date.value));
  } else if (_date.specialValue === 'first') {
    date.setDate(1);
  } else if (_date.specialValue === 'last') {
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
  } else {
    // 需要判断dateBackup.getDate()是否会超过设置月份的最大天数
    date.setDate(Math.min(getMaxDate(date), dateBackup.getDate()));
  }
  const hour = obj.H;
  if (hour.operator && hour.value) {
    // eslint-disable-next-line no-new-func
    date.setHours(Function(`"ue strict";return ${dateBackup.getHours()} ${hour.operator} ${hour.value}`)());
  } else if (hour.value) {
    date.setHours(hour.value);
  } else if (hour.specialValue === 'first') {
    date.setHours(0);
  } else if (hour.specialValue === 'last') {
    date.setHours(23);
  } else if (hour.contain) {
    date.setHours(dateBackup.getHours());
  } else {
    date.setHours(0);
  }
  const minute = obj.m;
  if (minute.operator && minute.value) {
    // eslint-disable-next-line no-new-func
    date.setMinutes(Function(`"ue strict";return ${dateBackup.getMinutes()} ${minute.operator} ${minute.value}`)());
  } else if (minute.value) {
    date.setMinutes(minute.value);
  } else if (minute.specialValue === 'first') {
    date.setMinutes(0);
  } else if (minute.specialValue === 'last') {
    date.setMinutes(59);
  } else if (minute.contain) {
    date.setMinutes(dateBackup.getMinutes());
  } else {
    date.setMinutes(0);
  }
  const second = obj.s;
  if (second.operator && second.value) {
    // eslint-disable-next-line no-new-func
    date.setSeconds(Function(`"ue strict";return ${dateBackup.getSeconds()} ${second.operator} ${second.value}`)());
  } else if (second.value) {
    date.setSeconds(second.value);
  } else if (second.specialValue === 'first') {
    date.setSeconds(0);
  } else if (second.specialValue === 'last') {
    date.setSeconds(59);
  } else if (second.contain) {
    date.setSeconds(dateBackup.getSeconds());
  } else {
    date.setSeconds(0);
  }
  return date;
}
/**
 * 改变元素在列表中的顺序
 * @param list 列表
 * @param item 元素
 * @param num 位移，向前移动是负数，向后移动是正数，如-1表示向前移动一位
 * @return {boolean} true表示移动成功，false表示没有移动
 */
export function changeOrder ({list, item, num}) {
  const index = list.indexOf(item);
  if (num === 0) {
    return false;
  }
  if ((num < 0 && index < 1) || (num > 0 && index > list.length - 1)) {
    return false;
  }
  list.splice(index, 1);
  list.splice(index + num, 0, item);
  return true;
}
/**
 * 验证表单的字段是否已经存在相同的记录
 * @param isExistApi    验证的API
 * @param editTd        对象ID
 * @param rowName       属性名
 * @param value         属性值
 * @param callback      验证成功的回调方法
 * @param config      isExistApi相关配置
 * @returns {*}
 */
export function validateIsExist (isExistApi, editTd, rowName, value, callback, config = {showLoading: false}) {
  if (!value) {
    return callback(new Error('此项不能为空'));
  }
  isExistApi({ id: editTd, rowName: rowName, rowValue: value }, config).then(response => {
    const res = response.data;
    if (res.success) {
      return callback(new Error(res.msg));
    } else {
      callback();
    }
  });
}
// 过滤掉空值，返回正常的表单数据
export function filterEmptyData (queryParams) {
  const paramData = {};
  for (const p in queryParams) {
    if (isNotEmpty(queryParams[p])) {
      paramData[p] = queryParams[p];
    }
  }
  return paramData;
}
/**
 * 获取对象在数组中的索引
 * @param arr           数组
 * @param obj           对象
 * @param key           判断对象相等的主键
 * @returns {Number}    返回索引值，未找到返回-1
 */
export function indexOfArr (arr, obj, key) {
  const len = arr.length;
  for (let i = 0; i < len; i++) {
    const o = arr[i];
    if (o[key] === obj[key]) {
      return i;
    }
  }
  return -1;
}
export function loadScript ({url, callback}) {
  // 记录script的加载结果， key是url, value是{loaded: true, task: []}, loaded表示script是否加载完毕，task表示等待的callback任务
  const scriptTask = {};
  let obj = scriptTask[url];
  if (!obj) {
    obj = {task: []};
    scriptTask[url] = obj;
  }
  if (obj.loaded === true) {
    callback && callback();
    return;
  }
  if (callback) {
    obj.task.push(callback);
  }
  const script = document.createElement('script');
  script.type = 'text/javascript';

  function handleLoad () {
    obj.loaded = true;
    script.removeEventListener('load', handleLoad);
    for (const item of obj.task) {
      item();
    }
    obj.task = [];
  }

  script.addEventListener('load', handleLoad);
  script.src = url;
  document.head.appendChild(script);
}
/**
 * 数组转换为逗号分隔的字符串
 * @param arr           数组
 * @param paramName     数组的其中一个属性名
 * @returns {string}    逗号分隔的字符串
 */
export function arrayToString (arr, paramName) {
  let ids = '';
  for (let i = 0; i < arr.length; i++) {
    const row = arr[i];
    if (paramName) {
      ids += row[paramName] + ',';
    } else {
      ids += row + ',';
    }
  }
  // 去掉末尾逗号
  if (ids.indexOf(',') > -1) {
    ids = ids.substr(0, ids.length - 1);
  }
  return ids;
}

/**
 * 计算文件的体积
 * @param size
 * @param fractionDigits 显示多少位小数
 * @return {*}
 */
export function getFileSizeStr ({size, fractionDigits = 2}) {
  if (!isNumber(size)) {
    throw new RuleError('size不是数字');
  }
  const unitList = ['B', 'KB', 'MB', 'GB', 'TB'];
  let index = 0;
  while (size > 1024) {
    size = size / 1024;
    index += 1;
  }
  return size.toFixed(2) + unitList[index];
}

export function getFileSize({size, unit}) {
  // 默认以B为单位
  let sizeNum = size;
  for (const unitItem of FILE_SIZE_UNITS) {
    sizeNum = sizeNum * 1024;
    if (unitItem.value === unit) {
      break;
    }
  }
  return sizeNum;
}

/**
 * 驼峰转下划线
 * @param str
 * @return {string}
 */
export function toUnderlineCase ({str}) {
  return str.replace(/(A-Z)/g, '_$1').toLowerCase();
}

/**
 * 下划线转驼峰
 * @param str
 * @return {string}
 */
export function toCamelCase ({str}) {
  return str.replace(/_(\w)/g, function (all, letter) {
    return letter.toUpperCase();
  });
}

/**
 * 判断array是不是一个空数组或者不是数组
 * @param array
 * @returns {boolean}
 */
export function isEmptyArray({array}) {
  return !Array.isArray(array) || array.length < 1;
}

/**
 * 递归复制对象属性
 * @param target
 * @param source
 * @param getArrayItemKey
 */
export function mergeObject ({target, source, getArrayItemKey}) {
  function mergeObjectRecursive ({target, source}) {
    if (target === source) {
      return;
    }
    for (const key in source) {
      const sourceValue = source[key];
      let targetValue = target[key];
      if (sourceValue === targetValue) {
        continue;
      }
      // 要判断value是null, null instanceof Object也是为true, undefined的情况
      if (sourceValue === null || sourceValue === undefined || isFunction(sourceValue) || isString(sourceValue) || isNumber(sourceValue) || isBoolean(sourceValue) || sourceValue instanceof Date) {
        target[key] = sourceValue;
        continue;
      }
      if (Array.isArray(sourceValue)) {
        if (!Array.isArray(targetValue)) {
          target[key] = targetValue = [];
        }
        // 数组合并，如果数组元素有唯一标识，使用唯一标识来匹配会更加合理
        // 需要考虐在sourceValue中存在的元素，在targetValue中不存在的元素
        const array = [];
        for (let i = 0; i < sourceValue.length; i++) {
          const item = sourceValue[i];
          const valueKey = getArrayItemKey ? getArrayItemKey({array: sourceValue, item}) : i;
          let flag = false;
          for (let j = 0; j < targetValue.length; j++) {
            let innerItem = targetValue[j];
            if (item === innerItem) {
              flag = true;
              continue;
            }
            const targetValueKey = getArrayItemKey ? getArrayItemKey({array: targetValue, item: innerItem}) : j;
            if (valueKey !== targetValueKey) {
              continue;
            }
            flag = true;
            if (item === null || item === undefined || isFunction(item) || isString(item) || isNumber(item) || isBoolean(item) || sourceValue instanceof Date) {
              targetValue[j] = item;
              continue;
            }
            if (Array.isArray(item)) {
              if (!Array.isArray(innerItem)) {
                targetValue[j] = innerItem = [];
              }
              mergeObjectRecursive({target: innerItem, source: item});
              continue;
            }
            if (isObject(item)) {
              if (!isObject(innerItem) || !innerItem) {
                targetValue[j] = innerItem = {};
              }
              mergeObjectRecursive({target: innerItem, source: item});
              continue;
            }
          }
          if (!flag) {
            array.push(item);
          }
        }
        if (array.length > 0) {
          for (const item of array) {
            if (item === null || item === undefined || isFunction(item) || isString(item) || isNumber(item) || isBoolean(item) || sourceValue instanceof Date) {
              targetValue.push(item);
              continue;
            }
            if (Array.isArray(item)) {
              targetValue.push([]);
              mergeObjectRecursive({target: targetValue[targetValue.length - 1], source: item});
              continue;
            }
            if (isObject(item)) {
              targetValue.push({});
              mergeObjectRecursive({target: targetValue[targetValue.length - 1], source: item});
              continue;
            }
            // 其它情况就直接放入数组
            targetValue.push(item);
          }
        }
        continue;
      }
      if (isObject(sourceValue)) {
        if (!isObject(targetValue) || !targetValue) {
          target[key] = targetValue = {};
        }
        mergeObjectRecursive({target: targetValue, source: sourceValue});
        continue;
      }
      target[key] = sourceValue;
    }
  }
  mergeObjectRecursive({target, source});
}

/**
 * 树转成数组
 * @param treeNode
 * @param treeNodeList
 * @param childrenProperty
 * @param validate 是否转换的校验函数，返回true/false
 * @return {*[]}
 */
export function treeToArray({treeNode, treeNodeList, childrenProperty = 'children', validate}) {
  const arr = [];
  let list = treeNodeList;
  if (!Array.isArray(treeNodeList)) {
    list = [treeNode];
  }
  function treeToArrayRecursive({list, childrenProperty, validate}) {
    for (const item of list) {
      if (validate && !validate(item)) {
        continue;
      }
      arr.push(item);
      if (Array.isArray(item[childrenProperty])) {
        treeToArrayRecursive({list: item[childrenProperty], childrenProperty, validate});
      }
    }
  }
  treeToArrayRecursive({list, childrenProperty, validate});
  return arr;
}

/**
 * 将树转成二维数组
 * @param tree
 * @param childrenProperty
 * @param validate 是否转换的校验函数，返回true/false
 * @return {array}
 */
export function treeToMultiDimensionArray ({tree, childrenProperty = 'children', validate}) {
  const multiDimensionArray = [];
  function treeToMultiDimensionArrayRecursive (tree, deep) {
    if (!Array.isArray(tree) || tree.length < 1) {
      return;
    }
    // 判断某一层是否有对应的数组，没有就初始化一个
    let array = multiDimensionArray[deep];
    if (!Array.isArray(array)) {
      array = [];
      multiDimensionArray[deep] = array;
    }
    deep += 1;
    for (const item of tree) {
      // 没有传入校验函数，就直接加入array中
      // 有校验函数，就需要通过校验才可以加入array
      if (validate && !validate(item)) {
        continue;
      }
      array.push(item);
      treeToMultiDimensionArrayRecursive(item[childrenProperty], deep);
    }
  }
  treeToMultiDimensionArrayRecursive(tree, 0);
  return multiDimensionArray;
}

/**
 * @desc 获取树的深度
 * @param tree 数组
 * @param childrenProperty 子节点的属性名，默认是children
 * @return {number} 返回深度值，如[]空数组是0， [{}]有一个元素是1, [{children[]}]也是1，[{children[{}]}]也是2
 */
export function getTreeDeep ({tree, childrenProperty = 'children'}) {
  let maxDeep = 0;
  function getTreeDeepRecursive (tree, deep) {
    if (!Array.isArray(tree) || tree.length < 1) {
      return;
    }
    deep += 1;
    if (deep > maxDeep) {
      maxDeep = deep;
    }
    for (const item of tree) {
      getTreeDeepRecursive(item[childrenProperty], deep);
    }
  }
  getTreeDeepRecursive(tree, 0);
  return maxDeep;
}

/**
 * 将数组转成逗号分隔字符串
 * @param source
 * @param rowKey
 * @return {string|*}
 */
export function fromArrayToCommaDivideString({ source, rowKey }) {
  if (Array.isArray(source)) {
    const arr = [];
    for (const item of source) {
      arr.push(item[rowKey]);
    }
    return arr.join(',');
  }
  return source;
}

/**
 * 将逗号分隔字符串转成数组
 * @param source
 * @param rowKey
 * @return {*[]|any}
 */
export function fromCommaDivideStringToArray({ source, rowKey }) {
  // 已经是数组就不需要转换了
  if (Array.isArray(source)) {
    return source;
  }
  if (isEmpty(source)) {
    return [];
  }
  const arr = source.split(',');
  const newArr = [];
  for (const item of arr) {
    newArr.push({[rowKey]: item});
  }
  return newArr;
}

/**
 * 将数组转成json字符串
 * @param source
 * @param rowKey
 * @return {string|*}
 */
export function fromArrayToJsonString({ source, rowKey }) {
  if (Array.isArray(source)) {
    const arr = [];
    for (const item of source) {
      arr.push(item[rowKey]);
    }
    return JSON.stringify(arr);
  }
  return source;
}

/**
 * 将json字符串转成数组
 * @param source
 * @param rowKey
 * @return {*[]|any}
 */
export function fromJsonStringToArray({ source, rowKey }) {
  // 已经是数组就不需要转换了
  if (Array.isArray(source)) {
    return source;
  }
  if (isEmpty(source)) {
    return [];
  }
  const arr = JSON.parse(source);
  const newArr = [];
  for (const item of arr) {
    newArr.push({[rowKey]: item});
  }
  return newArr;
}

/**
 * 判断节点是不是叶子节点
 * @param node
 * @param childrenProperty
 * @return {boolean}
 */
export function isLeaf({node, childrenProperty = 'children'}) {
  const children = node[childrenProperty];
  return !Array.isArray(children) || children.length < 1;
}

/**
 * 去掉字符串内所有空格
 * @param str
 * @return {*}
 */
export function trimAll(str) {
  if (isEmpty(str)) {
    return str;
  }
  return str.replace(/\s*/g, '');
}

/**
 * 递归对比reference和target是否相同
 * @param reference 参考对象
 * @param target 目标对象
 * @param ignoreProperties 忽略比较的属性
 * @return {boolean}
 */
function compareRecursive({reference, target, ignoreProperties = []}) {
  if (reference === target) {
    return true;
  }
  if (isPlainObject(reference) && isPlainObject(target)) {
    for (const key in reference) {
      if (ignoreProperties.includes(key)) {
        continue;
      }
      if (!Object.prototype.hasOwnProperty.call(target, key)) {
        return false;
      }
      if (!compareRecursive({reference: reference[key], target: target[key], ignoreProperties})) {
        return false;
      }
    }
    return true;
  }
  return false;
}

/**
 * 对比value1和value2是否相同
 * @param value1
 * @param value2
 * @param ignoreProperties 忽略比较的属性
 * @return {boolean}
 */
export function compare({value1, value2, ignoreProperties = []}) {
  return compareRecursive({reference: value1, target: value2, ignoreProperties}) && compareRecursive({reference: value2, target: value1, ignoreProperties});
}

/**
 * 获取随机字符串
 * @param length 字符创长度
 * @param arr 可选字符类型的二维数组，数组额每个元素是对应类型的可选字符
 * @param typeLimit 字符类型数量的最小值，即拼接字符串最少要用到多少种类型的字符，typeLimit 小于等于 arr的长度 且 大于 0, 默认值是arr的长度
 * @return {string}
 */
export function createRandomStr({length, arr = [UPPER_CASE_LETTER, LOWER_CASE_LETTER, DIGITAL_LETTER, SPECIAL_LETTER], typeLimit = arr.length}) {
  let count = 0;
  while (true) {
    // 创建字符串
    let str = '';
    for (let i = 0; i < length; i++) {
      const typeRandom = Math.floor(Math.random() * arr.length);
      const arrItem = arr[typeRandom];
      const letterRandom = Math.floor(Math.random() * arrItem.length);
      str += arrItem[letterRandom];
    }
    // 校验字符串是否包含typeLimit指定的类型数量的字符
    if (checkRandomStr({str, length, arr, typeLimit})) {
      return str;
    }
    count++;
    console.log('创建的随机字符串校验不通过', str);
    if (count > 100) {
      // 循环次数大于100次，就要退出，防止某些地方异常导致死循环
      break;
    }
  }
  throw new Error('创建随机字符串异常');
}

/**
 * 校验随机字符串是否符合规范
 * @param str 需要检验的字符串
 * @param length 长度限制，字符串的长度需要大于等于 length
 * @param arr 可选字符类型的二维数组，数组额每个元素是对应类型的可选字符
 * @param typeLimit 字符类型数量的最小值，即拼接字符串最少要用到多少种类型的字符，typeLimit 小于等于 arr的长度 且 大于 0, 默认值是arr的长度
 * @return {boolean}
 */
export function checkRandomStr({str, length, arr = [UPPER_CASE_LETTER, LOWER_CASE_LETTER, DIGITAL_LETTER, SPECIAL_LETTER], typeLimit = arr.length}) {
  if (str.length < length) {
    return false;
  }
  const typeArr = [];
  for (let i = 0; i < arr.length; i++) {
    typeArr.push(0);
  }
  for (let i = 0; i < str.length; i++) {
    const letter = str.charAt(i);
    for (let j = 0; j < arr.length; j++) {
      if (arr[j].includes(letter)) {
        typeArr[j] = 1;
        break;
      }
    }
  }
  return typeArr.reduce((previousValue, currentValue) => { return previousValue + currentValue; }) >= typeLimit;
}

/**
 * 计算天数差
 * @param date 目标的日期， 可以是Date类型，也可以是时间戳
 * @param dateRef 对比的日期， 可以是Date类型，也可以是时间戳
 * @return {number}
 */
export function dateDiff({date, dateRef}) {
  let t1 = date;
  if (date instanceof Date) {
    t1 = date.getTime();
  }
  let t2 = dateRef;
  if (dateRef instanceof Date) {
    t2 = dateRef.getTime();
  }
  // 86400000 = 24 * 60 * 60 * 1000;
  return Math.floor((t1 - t2) / 86400000);
}

/**
 * 默认发送日志的方法
 * @param json
 * @return {Promise<Response>}
 */
export const defaultRemote = function (json) {
  return window.fetch('/api/ops/front/log/save', {
    method: 'POST',
    body: JSON.stringify(json),
    headers: new Headers({
      'Content-Type': 'application/json'
    })
  });
};
/**
 * 遍历树结构
 * @param tree 树
 * @param childrenProperty 子节点集合的属性名称
 * @param parent 父节点
 * @param callback 回调函数
 */
export const traversalTree = function ({tree, childrenProperty = 'children', parent, callback}) {
  if (!Array.isArray(tree)) {
    return;
  }
  for (const item of tree) {
    callback({item, tree, parent});
    if (Object.prototype.hasOwnProperty.call(item, childrenProperty)) {
      traversalTree({tree: item[childrenProperty], childrenProperty, parent: item, callback});
    }
  }
};
const ua = navigator.userAgent.toLowerCase();
const testUa = regexp => regexp.test(ua);
const testVs = regexp => (ua.match(regexp) + '')
  .replace(/[^0-9|_.]/ig, '')
  .replace(/_/ig, '.');
const getEngineAndSupporter = () => {
  // 内核和载体
  let engine = 'unknow';
  let supporter = 'unknow';
  if (testUa(/applewebkit/ig) && testUa(/safari/ig)) {
    engine = 'webkit'; // webkit内核
    if (testUa(/edge/ig)) {
      supporter = 'edge'; // edge浏览器
    } else if (testUa(/opr/ig)) {
      supporter = 'opera'; // opera浏览器
    } else if (testUa(/chrome/ig)) {
      supporter = 'chrome'; // chrome浏览器
    } else {
      supporter = 'safari'; // safari浏览器
    }
  } else if (testUa(/gecko/ig) && testUa(/firefox/ig)) {
    engine = 'gecko'; // gecko内核
    supporter = 'firefox'; // firefox浏览器
  } else if (testUa(/presto/ig)) {
    engine = 'presto'; // presto内核
    supporter = 'opera'; // opera浏览器
  } else if (testUa(/trident|compatible|msie/ig)) {
    engine = 'trident'; // trident内核
    supporter = 'iexplore'; // iexplore浏览器
  }

  // 内核版本
  let engineVersion = 'unknow';
  if (engine === 'webkit') {
    engineVersion = testVs(/applewebkit\/[\d.]+/ig);
  } else if (engine === 'gecko') {
    engineVersion = testVs(/gecko\/[\d.]+/ig);
  } else if (engine === 'presto') {
    engineVersion = testVs(/presto\/[\d.]+/ig);
  } else if (engine === 'trident') {
    engineVersion = testVs(/trident\/[\d.]+/ig);
  }

  // 载体版本
  let supporterVersion = 'unknow';
  if (supporter === 'chrome') {
    supporterVersion = testVs(/chrome\/[\d.]+/ig);
  } else if (supporter === 'safari') {
    supporterVersion = testVs(/version\/[\d.]+/ig);
  } else if (supporter === 'firefox') {
    supporterVersion = testVs(/firefox\/[\d.]+/ig);
  } else if (supporter === 'opera') {
    supporterVersion = testVs(/opr\/[\d.]+/ig);
  } else if (supporter === 'iexplore') {
    supporterVersion = testVs(/(msie [\d.]+)|(rv:[\d.]+)/ig);
  } else if (supporter === 'edge') {
    supporterVersion = testVs(/edge\/[\d.]+/ig);
  }
  return {
    engine,
    supporter,
    engineVersion,
    supporterVersion
  };
}

const getShell = () => {
  // 外壳和外壳版本
  let shell = 'none';
  let shellVersion = 'unknow';
  if (testUa(/micromessenger/ig)) {
    shell = 'wechat'; // 微信浏览器
    shellVersion = testVs(/micromessenger\/[\d.]+/ig);
  } else if (testUa(/qqbrowser/ig)) {
    shell = 'qq'; // QQ浏览器
    shellVersion = testVs(/qqbrowser\/[\d.]+/ig);
  } else if (testUa(/ubrowser/ig)) {
    shell = 'uc'; // UC浏览器
    shellVersion = testVs(/ubrowser\/[\d.]+/ig);
  } else if (testUa(/2345explorer/ig)) {
    shell = '2345'; // 2345浏览器
    shellVersion = testVs(/2345explorer\/[\d.]+/ig);
  } else if (testUa(/metasr/ig)) {
    shell = 'sougou'; // 搜狗浏览器
  } else if (testUa(/lbbrowser/ig)) {
    shell = 'liebao'; // 猎豹浏览器
  } else if (testUa(/maxthon/ig)) {
    shell = 'maxthon'; // 遨游浏览器
    shellVersion = testVs(/maxthon\/[\d.]+/ig);
  } else if (testUa(/bidubrowser/ig)) {
    shell = 'baidu'; // 百度浏览器
    shellVersion = testVs(/bidubrowser [\d.]+/ig);
  }
  return {
    shell,
    shellVersion
  };
}
const getSystem = () => {
  let system = 'unknown';
  if (testUa(/windows|win32|win64|wow32|wow64/ig)) {
    system = 'windows'; // window系统
  } else if (testUa(/macintosh|macintel/ig)) {
    system = 'osx'; // osx系统
  } else if (testUa(/x11/ig)) {
    system = 'linux'; // linux系统
  } else if (testUa(/android|adr/ig)) {
    system = 'android'; // android系统
  } else if (testUa(/ios|iphone|ipad|ipod|iwatch/ig)) {
    system = 'ios'; // ios系统
  }
  // 系统版本
  let systemVersion = 'unknown';
  if (system === 'windows') {
    if (testUa(/windows nt 5.0|windows 2000/ig)) {
      systemVersion = '2000';
    } else if (testUa(/windows nt 5.1|windows xp/ig)) {
      systemVersion = 'xp';
    } else if (testUa(/windows nt 5.2|windows 2003/ig)) {
      systemVersion = '2003';
    } else if (testUa(/windows nt 6.0|windows vista/ig)) {
      systemVersion = 'vista';
    } else if (testUa(/windows nt 6.1|windows 7/ig)) {
      systemVersion = '7';
    } else if (testUa(/windows nt 6.2|windows 8/ig)) {
      systemVersion = ' "8"';
    } else if (testUa(/windows nt 6.3|windows 8.1/ig)) {
      systemVersion = '8.1';
    } else if (testUa(/windows nt 10.0|windows 10/ig)) {
      systemVersion = '10';
    }
  } else if (system === 'osx') {
    systemVersion = testVs(/os x [\d._]+/ig);
  } else if (system === 'android') {
    systemVersion = testVs(/android [\d._]+/ig);
  } else if (system === 'ios') {
    systemVersion = testVs(/os [\d._]+/ig);
  }
  return {
    system,
    systemVersion
  };
}

const getPlatform = (system) => {
  // 平台
  let platform = 'unknow';
  if (system === 'windows' || system === 'osx' || system === 'linux') {
    platform = 'desktop'; // 桌面端
  } else if (system === 'android' || system === 'ios' || testUa(/mobile/ig)) {
    platform = 'mobile'; // 移动端
  }
  return platform;
}
export function browserInfo() {
// 权重：系统 + 系统版本 > 平台 > 内核 + 载体 + 内核版本 + 载体版本 > 外壳 + 外壳版本
  const sys = getSystem(); // 系统信息
  const platform = getPlatform(sys.system); // 平台
  const engine = getEngineAndSupporter(); // 引擎和浏览器
  const shell = getShell(); // 外壳
  const __info__ = Object.assign({platform}, sys, engine, shell);

  return __info__;
}
export const collectErrorInfo = ({err, vm, info}) => {
  const obj = {};
  if (err instanceof Error) {
    obj.message = err.message;
    obj.stack = err.stack;
  } else {
    obj.message = err;
  }
  if (vm) {
    obj.vmName = vm.$options.name || '';
  }
  obj.info = info || '';
  obj.url = window.location.href;
  obj.browser = JSON.stringify(browserInfo());
  obj.appName = 'plant-admin';
  return obj;
};
/**
 * 数字转大写
 * @param num
 * @returns {string}
 */
export const convertBigDigit = ({num}) => {
  if (isEmpty(num)) {
    return '';
  }
  // 输入验证
  if (isNaN(num)) {
    return '输入无效';
  }
  const cnNums = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
  const cnIntRadices = ['', '拾', '佰', '仟'];
  const cnIntUnits = ['', '万', '亿', '兆'];
  const cnDecUnits = ['角', '分', '毫', '厘'];
  const cnInteger = '整';
  const cnIntLast = '元';
  // 处理负数并保留4位小数
  num = Math.abs(parseFloat(num)).toFixed(4);
  const parts = num.split('.');
  let integerStr = parts[0];
  const decimalStr = parts[1] || '';
  let chineseStr = '';

  // 整数部分处理
  if (parseInt(integerStr) > 0) {
    let zeroCount = 0;
    const intLen = integerStr.length;

    for (let i = 0; i < intLen; i++) {
      const digit = parseInt(integerStr.substr(i, 1));
      const position = intLen - i - 1;
      const unitIdx = Math.floor(position / 4);
      const radixIdx = position % 4;

      if (digit === 0) {
        zeroCount++;
      } else {
        if (zeroCount > 0) {
          chineseStr += cnNums[0];
          zeroCount = 0;
        }
        chineseStr += cnNums[digit] + cnIntRadices[radixIdx];
      }

      if (radixIdx === 0 && zeroCount < 4) {
        chineseStr += cnIntUnits[unitIdx];
      }
    }
    chineseStr += cnIntLast;
  }

  // 小数部分处理
  if (decimalStr !== '0000') {
    for (let i = 0; i < Math.min(decimalStr.length, 4); i++) {
      const digit = parseInt(decimalStr.substring(i, i + 1));
      if (digit !== 0) {
        chineseStr += cnNums[digit] + cnDecUnits[i];
      }
    }
  } else if (chineseStr !== '') {
    chineseStr += cnInteger;
  }

  return chineseStr || cnNums[0] + cnIntLast + cnInteger;
};
/**
 * 生成随机字符串
 * @param length
 * @returns {string}
 */
export const randomString = ({length}) => {
  const base = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const arr = [];
  for (let i = 0; i < length; i++) {
    arr.push(base.charAt(Math.floor(Math.random() * base.length)));
  }
  return arr.join('');
};
/**
 * 身份证号校验
 * @param rule
 * @param value
 * @param callback
 */
export const idNumValidator = (rule, value, callback) => {
  if (rule.required && isEmpty(value)) {
    callback(new RuleError('请输入身份证号'));
    return;
  }
  if (!isIdNum(value)) {
    callback(new RuleError('请输入正确的身份证号'));
    return;
  }
  callback();
};